import { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import ReactDOM from 'react-dom';
import { useLanguage } from '../LanguageContext';
import translations from '../translations';
import { formatAmount, convertIngredient } from '../utils/unitConverter';
import './CookingMode.css';

// Phrases that signal a step runs in parallel with the previous one
const PARALLEL_PHRASES = [
  'meanwhile',
  'in the meantime',
  'at the same time',
  'simultaneously',
  'whilst',
  'while the', 'while this', 'while that', 'while your', 'while it',
  'during this time', 'during this',
  'as the', 'as this', 'as it', 'as you', 'as your',
];

function isParallelStep(instruction) {
  const lower = instruction.trim().toLowerCase();
  return PARALLEL_PHRASES.some(phrase => lower.startsWith(phrase));
}

// Group steps into slides — parallel steps join the previous slide
function groupStepsIntoSlides(steps) {
  const slides = [];
  for (const step of steps) {
    if (isParallelStep(step.instruction) && slides.length > 0) {
      slides[slides.length - 1].push(step);
    } else {
      slides.push([step]);
    }
  }
  return slides;
}

// Normalise tips to [{text, step}] — handles both old (string[]) and new ({text,step}[]) formats
function normaliseTips(tips, totalSteps) {
  if (!tips || !tips.length) return [];
  if (typeof tips[0] === 'string') {
    // Old format — attach all tips to the last step
    return tips.map(text => ({ text, step: totalSteps }));
  }
  return tips;
}

export default function CookingMode({ recipe, steps, ingredients, scale, unitSystem, tips: rawTips, onClose }) {
  const { language } = useLanguage();
  const t = translations[language];

  // Group steps into slides (parallel steps on same slide)
  const slides = useMemo(() => groupStepsIntoSlides(steps), [steps]);

  // Normalise tips and index them by step number
  const tips = useMemo(() => normaliseTips(rawTips, steps.length), [rawTips, steps.length]);

  const [slideIndex, setSlideIndex] = useState(0);
  const [tab, setTab] = useState('steps'); // 'steps' | 'ingredients'
  const [timerSeconds, setTimerSeconds] = useState(null);
  const [timerRunning, setTimerRunning] = useState(false);
  const [timerDone, setTimerDone] = useState(false);
  const wakeLockRef = useRef(null);
  const intervalRef = useRef(null);

  const currentSlide = slides[slideIndex]; // array of 1 or more steps
  const isLastSlide = slideIndex === slides.length - 1;

  // Timer uses the longest duration in the current slide
  const timerStep = currentSlide
    ? [...currentSlide].sort((a, b) => (b.duration_mins || 0) - (a.duration_mins || 0))[0]
    : null;

  // Lock body scroll while cooking mode is open
  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = prev; };
  }, []);

  // Wake Lock
  useEffect(() => {
    async function acquireWakeLock() {
      if ('wakeLock' in navigator) {
        try {
          wakeLockRef.current = await navigator.wakeLock.request('screen');
        } catch (_) {}
      }
    }
    acquireWakeLock();
    return () => {
      if (wakeLockRef.current) {
        wakeLockRef.current.release().catch(() => {});
        wakeLockRef.current = null;
      }
    };
  }, []);

  // Keyboard nav
  useEffect(() => {
    function onKey(e) {
      if (e.key === 'Escape') { onClose(); return; }
      if (tab !== 'steps') return;
      if (e.key === 'ArrowRight' && !isLastSlide) setSlideIndex(i => i + 1);
      if (e.key === 'ArrowLeft' && slideIndex > 0) setSlideIndex(i => i - 1);
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose, tab, isLastSlide, slideIndex]);

  // Timer logic
  const initTimer = useCallback((step) => {
    clearInterval(intervalRef.current);
    if (step?.duration_mins) {
      setTimerSeconds(step.duration_mins * 60);
    } else {
      setTimerSeconds(null);
    }
    setTimerRunning(false);
    setTimerDone(false);
  }, []);

  useEffect(() => {
    initTimer(timerStep);
  }, [slideIndex, timerStep, initTimer]);

  useEffect(() => {
    if (timerRunning) {
      intervalRef.current = setInterval(() => {
        setTimerSeconds(s => {
          if (s <= 1) {
            clearInterval(intervalRef.current);
            setTimerRunning(false);
            setTimerDone(true);
            return 0;
          }
          return s - 1;
        });
      }, 1000);
    } else {
      clearInterval(intervalRef.current);
    }
    return () => clearInterval(intervalRef.current);
  }, [timerRunning]);

  useEffect(() => {
    if (!timerDone) return;
    try {
      const ctx = new (window.AudioContext || window.webkitAudioContext)();
      const beep = (startTime, freq, duration) => {
        const osc = ctx.createOscillator();
        const gain = ctx.createGain();
        osc.connect(gain);
        gain.connect(ctx.destination);
        osc.type = 'sine';
        osc.frequency.value = freq;
        gain.gain.setValueAtTime(0.35, startTime);
        gain.gain.exponentialRampToValueAtTime(0.001, startTime + duration);
        osc.start(startTime);
        osc.stop(startTime + duration);
      };
      const t = ctx.currentTime;
      beep(t,        880, 0.15);
      beep(t + 0.2,  880, 0.15);
      beep(t + 0.4, 1100, 0.3);
    } catch (e) {}
  }, [timerDone]);

  function goTo(index) {
    setSlideIndex(index);
  }

  function formatTimer(secs) {
    const m = Math.floor(secs / 60);
    const s = secs % 60;
    return `${m}:${s.toString().padStart(2, '0')}`;
  }

  function resetTimer() {
    clearInterval(intervalRef.current);
    setTimerRunning(false);
    setTimerDone(false);
    setTimerSeconds(timerStep?.duration_mins ? timerStep.duration_mins * 60 : null);
  }

  return ReactDOM.createPortal(
    <div className="cooking-mode">
      {/* Header */}
      <div className="cooking-mode__header">
        <span className="cooking-mode__recipe-title">{recipe.title}</span>
        <div className="cooking-mode__header-tabs">
          <button
            className={`cooking-mode__tab${tab === 'steps' ? ' cooking-mode__tab--active' : ''}`}
            onClick={() => setTab('steps')}
          >
            {t.cookingModeSteps}
          </button>
          <button
            className={`cooking-mode__tab${tab === 'ingredients' ? ' cooking-mode__tab--active' : ''}`}
            onClick={() => setTab('ingredients')}
          >
            {t.cookingModeIngredients}
          </button>
        </div>
        <button className="cooking-mode__close" onClick={onClose} aria-label="Exit cooking mode">
          {t.cookingModeClose}
        </button>
      </div>

      {/* Progress bar */}
      {tab === 'steps' && (
        <div className="cooking-mode__progress-bar">
          <div
            className="cooking-mode__progress-fill"
            style={{ width: `${((slideIndex + 1) / slides.length) * 100}%` }}
          />
        </div>
      )}

      {/* Ingredients tab */}
      {tab === 'ingredients' && (
        <div className="cooking-mode__ingredients">
          <ul className="cooking-mode__ingredients-list">
            {ingredients.map((ing, i) => {
              const scaled = ing.amount !== null ? ing.amount * scale : null;
              const converted = convertIngredient(scaled, ing.unit, unitSystem);
              const displayAmt = formatAmount(converted.amount);
              const displayUnit = (!converted.unit || converted.unit === 'count') ? '' : converted.unit;
              return (
                <li key={ing.id || i} className="cooking-mode__ingredient">
                  <span className="cooking-mode__ingredient-amount">
                    {displayAmt}{displayUnit ? ` ${displayUnit}` : ''}
                  </span>
                  <span className="cooking-mode__ingredient-name">
                    {ing.ingredient_name}
                    {ing.prep_notes && <span className="cooking-mode__ingredient-prep">, {ing.prep_notes}</span>}
                  </span>
                </li>
              );
            })}
          </ul>
        </div>
      )}

      {/* Steps tab */}
      {tab === 'steps' && currentSlide && (
        <div className="cooking-mode__step-area">
          <div className="cooking-mode__step-counter">
            {t.cookingModeStepOf(slideIndex + 1, slides.length)}
          </div>

          <div className="cooking-mode__step-content">
            {currentSlide.length === 1 ? (
              // Single step — normal display
              <p className="cooking-mode__step-text">{currentSlide[0].instruction}</p>
            ) : (
              // Parallel steps — show all together
              <div className="cooking-mode__parallel-steps">
                {currentSlide.map((step, i) => (
                  <div key={step.id || i} className={`cooking-mode__parallel-step${i > 0 ? ' cooking-mode__parallel-step--meanwhile' : ''}`}>
                    {i > 0 && (
                      <div className="cooking-mode__meanwhile-label">At the same time</div>
                    )}
                    <p className="cooking-mode__step-text cooking-mode__step-text--parallel">{step.instruction}</p>
                  </div>
                ))}
              </div>
            )}

            {/* Tips for this step */}
            {(() => {
              const stepNums = currentSlide.map(s => s.step_number);
              const slideTips = tips.filter(tip => stepNums.includes(tip.step));
              if (!slideTips.length) return null;
              return (
                <div className="cooking-mode__tips">
                  {slideTips.map((tip, i) => (
                    <div key={i} className="cooking-mode__tip">
                      <span className="cooking-mode__tip-icon">💡</span>
                      <span className="cooking-mode__tip-text">{tip.text}</span>
                    </div>
                  ))}
                </div>
              );
            })()}

            {/* Timer */}
            {timerSeconds !== null && (
              <div className={`cooking-mode__timer${timerDone ? ' cooking-mode__timer--done' : ''}`}>
                <span className="cooking-mode__timer-display">{formatTimer(timerSeconds)}</span>
                <div className="cooking-mode__timer-controls">
                  {!timerRunning && timerSeconds > 0 && (
                    <button className="cooking-mode__timer-btn" onClick={() => setTimerRunning(true)}>
                      {t.cookingModeStartTimer}
                    </button>
                  )}
                  {timerRunning && (
                    <button className="cooking-mode__timer-btn" onClick={() => setTimerRunning(false)}>
                      {t.cookingModePauseTimer}
                    </button>
                  )}
                  <button className="cooking-mode__timer-btn cooking-mode__timer-btn--reset" onClick={resetTimer}>
                    {t.cookingModeResetTimer}
                  </button>
                </div>
              </div>
            )}
          </div>

          {/* Step dots */}
          <div className="cooking-mode__dots">
            {slides.map((_, i) => (
              <button
                key={i}
                className={`cooking-mode__dot${i === slideIndex ? ' cooking-mode__dot--active' : i < slideIndex ? ' cooking-mode__dot--done' : ''}`}
                onClick={() => goTo(i)}
                aria-label={`Step ${i + 1}`}
              />
            ))}
          </div>

          {/* Nav buttons */}
          <div className="cooking-mode__nav">
            <button
              className="cooking-mode__nav-btn cooking-mode__nav-btn--prev"
              onClick={() => setSlideIndex(i => i - 1)}
              disabled={slideIndex === 0}
            >
              {t.cookingModePrev}
            </button>
            {isLastSlide ? (
              <button className="cooking-mode__nav-btn cooking-mode__nav-btn--finish" onClick={onClose}>
                {t.cookingModeFinish}
              </button>
            ) : (
              <button
                className="cooking-mode__nav-btn cooking-mode__nav-btn--next"
                onClick={() => setSlideIndex(i => i + 1)}
              >
                {t.cookingModeNext}
              </button>
            )}
          </div>
        </div>
      )}
    </div>,
    document.body
  );
}
